import { Injectable } from '@angular/core';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { EstatePersisted } from '@app/dao/estate/estate.interface';
import {
  RentalType,
  TypeUrlEnum,
} from '@app/forms/funnel-audit-client/pr-funnel-estate/helper/pr-funnel-estate.types';
import { combineLatest, forkJoin, Observable, of, catchError, map, switchMap, tap } from 'rxjs';
import { LoanDto } from '../funnel-loan.service';
import { OwnerEnum, OwnershipTypeEnum } from '../funnel-ownership.service';
import {
  ExpenseDto,
  ExpenseTypeEnum,
  FUNNEL_RENTAL_EXPENSE_TYPES,
  InvestmentDto,
  PropertyDto,
} from './funnel-estate-shared.types';
import {
  findCurrentExpense,
  findCurrentRent,
  isSameAmount,
  toAnnualAmount,
  toMonthlyAmount,
} from './funnel-estate-transform.utils';

/**
 * Loi fiscale
 */
export enum FiscalLawEnum {
  FONCIER = 'FONCIER',
  MICRO_FONCIER = 'MICRO_FONCIER',
  BIC = 'BIC',
  MICRO_BIC = 'MICRO_BIC',
  MICRO_BIC_TOURISME_CLASSE = 'MICRO_BIC_TOURISME_CLASSE',
  MICRO_BIC_TOURISME_NON_CLASSE = 'MICRO_BIC_TOURISME_NON_CLASSE',
  PINEL = 'PINEL',
  PINEL_DOMTOM = 'PINEL_DOMTOM',
  CENSI_BOUVARD = 'CENSI_BOUVARD',
  MALRAUX = 'MALRAUX',
  MALRAUX_SECTEUR_SAUVE = 'MALRAUX_SECTEUR_SAUVE',
  MONUMENT_HISTO = 'MONUMENT_HISTO',
  BORLOO = 'BORLOO',
  DEFICITE_FONCIER = 'DEFICITE_FONCIER',
  DUFLOT = 'DUFLOT',
  DENORMANDIE = 'DENORMANDIE',
  ROBIEN = 'ROBIEN',
  SCELLIER = 'SCELLIER',
}

/**
 * Système fiscal LMNP/LMP
 */
export enum LmpLmnpTaxSystemEnum {
  MICRO_BIC = 'MICRO_BIC',
  REEL_SIMPLIFIE = 'REEL_SIMPLIFIE',
  REEL_NORMAL = 'REEL_NORMAL',
}

/**
 * Périodicité des loyers
 */
export enum PeriodicityEnum {
  MENS = 'MENS',
  TRIM = 'TRIM',
  SEM = 'SEM',
  AN = 'AN',
  ANNU = 'ANNU',
  ONE = 'ONE',
}

/**
 * Loi fiscale appliquée au bien
 */
export interface FiscalLawDto {
  id?: number;
  rentalInvestmentId?: number;
  type: FiscalLawEnum;
  startDate: string;
  endDate?: string;
  duration?: number;
  leftDuration?: number;
  priceAmount?: number;
  taxeGlobalAmount?: number;
  current?: boolean;
  typeTrad?: string;
}

/**
 * Contact du locataire
 */
export interface ContactTenantDto {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  rentAmount?: number;
}

/**
 * Loyer
 */
export interface RentDto {
  id?: number;
  accountId?: number;
  amount: number | string;
  startDate: string;
  endDate?: string;
  frenchRate?: number;
  frequency: PeriodicityEnum;
  contactTenant?: ContactTenantDto;
  rentType?: string;
  rentFiscalLaw?: string;
  rentedArea?: number;
}

/**
 * Bien locatif non meublé
 */
export interface UnfurnishedDto extends InvestmentDto {
  id?: number;
  userId: number;
  accountId: number;
  typeUrl: 'unfurnished'; // Toujours "unfurnished"
  productType: 'UNFURNISHED_RENTAL';

  // Informations sur le bien
  label: string;
  owner: OwnerEnum;
  ownershipType: OwnershipTypeEnum;
  currentOwnershipType?: OwnershipTypeEnum;
  dismemberKey?: number;
  dismemberDuration?: number;

  // Informations locatives
  rentalInvestmentId?: number;
  rentalType?: 'UNFURNISHED';

  // Propriété physique
  property: PropertyDto;

  // Loi fiscale
  fiscalLaw?: FiscalLawDto;

  // Loyers, dépenses, prêts
  rents?: RentDto[];
  expenses?: ExpenseDto[];
  loans?: LoanDto[];

  // Métadonnées
  realEstateType?: 'RENTAL';
  isNewProperty?: boolean;
  rentRatio?: number;
}

/**
 * Bien locatif meublé (LMNP / LMP)
 */
export interface LmnpLmpDto extends InvestmentDto {
  id?: number;
  userId: number;
  accountId: number;
  typeUrl: 'lmnp' | 'lmp';
  productType: 'LMP' | 'LMNP';

  // Informations sur le bien
  label: string;
  owner: OwnerEnum;
  ownershipType: OwnershipTypeEnum;
  currentOwnershipType?: OwnershipTypeEnum;
  dismemberKey?: number;
  dismemberDuration?: number;

  // Spécifique LMNP/LMP
  isLmp: boolean;
  isPurchaseHt?: boolean;
  tvaAmount?: number;
  furnitureValue?: number;
  typeTaxSystem?: LmpLmnpTaxSystemEnum;
  isHandled?: boolean;
  manager?: string;

  // Informations locatives
  rentalInvestmentId?: number;
  rentalType?: 'LMNP' | 'LMP';

  // Propriété physique
  property?: PropertyDto;

  // Loi fiscale
  fiscalLaw?: FiscalLawDto;

  // Loyers, dépenses, prêts
  rents?: RentDto[];
  expenses?: ExpenseDto[];
  loans?: LoanDto[];

  // Métadonnées
  realEstateType?: 'RENTAL';
  isNewProperty?: boolean;
  rentRatio?: number;
}

/**
 * Union type pour tous les types de biens locatifs
 */
export type BienLocatifDto = UnfurnishedDto | LmnpLmpDto;

type KeptRent = Pick<
  RentDto,
  | 'id'
  | 'startDate'
  | 'endDate'
  | 'rentType'
  | 'rentFiscalLaw'
  | 'rentedArea'
  | 'amount'
  | 'frequency'
>;

/**
 * Payload pour la création/modification d'un bien locatif
 * Note: Les rents, expenses et fiscalLaw sont gérés par le DAO via treatExtras()
 */
export interface BienLocatifPayload {
  id?: number;
  userId: number;
  accountId: number;
  typeUrl: 'lmplmnp' | 'unfurnished';
  productType: 'LMP_LMNP' | 'UNFURNISHED_RENTAL';
  label: string;
  owner: 'CLIENT';
  owners: Array<{ ownerId: number; share?: number }>;
  ownershipType: string;
  subscriptionSource?: string;
  purchasePrice: number;
  lastValue: number;
  startDate: string;
  current: boolean;
  property: PropertyDto;

  // Spécifique LMNP/LMP
  isLmp?: boolean;
  isPurchaseHt?: boolean;
  tvaAmount?: number;
  furnitureValue?: number;
  typeTaxSystem?: LmpLmnpTaxSystemEnum;
  isHandled?: boolean;
  manager?: string;

  // Collections (gérées par treatExtras)
  rents?: RentDto[];
  expenses?: ExpenseDto[];
  fiscalLaw?: FiscalLawDto;

  // Type original (pour détecter les changements de type)
  originalRentalType?: RentalType;
}

/**
 * Gestion des biens locatifs (LMNP et location non-meublée)
 *
 * Responsabilités :
 * - Charger les biens locatifs depuis /realestate/lmplmnp et /realestate/unfurnished
 * - Gérer le workflow complet de sauvegarde (bien + rents + expenses + fiscalLaw)
 * - Orchestrer le nettoyage des données lors de l'édition
 * - Supprimer un bien locatif
 *
 * Architecture :
 * Le DAO gère déjà la création des sous-ressources via postEstate() → treatExtras().
 * Ce service ajoute uniquement le nettoyage nécessaire lors de l'édition.
 *
 * Types supportés :
 * - FURNISHED (LMNP) → typeUrl = 'lmplmnp', productType = 'LMP_LMNP'
 * - UNFURNISHED (location nue) → typeUrl = 'unfurnished', productType = 'UNFURNISHED_RENTAL'
 */
@Injectable({
  providedIn: 'root',
})
export class FunnelBienLocatifService {
  constructor(private estateDao: EstateDao) {}

  /**
   * Point d'entrée principal : sauvegarde un bien locatif avec son workflow complet
   *
   * Workflow :
   * 1. Détection locale du changement de type (FURNISHED ↔ UNFURNISHED) via originalRentalType
   * 2. Si changement détecté : conversion du bien par le back (loyers, charges et crédits conservés)
   * 3. Nettoyage des charges gérées par le funnel, puis update normal
   * 4. Sauvegarde via DAO.postEstate() qui gère automatiquement :
   *    - editOrCreate() pour le bien
   *    - treatExtras() → locationEstateTreatment() pour rents/expenses/fiscalLaw
   *
   * @param payload Payload complet du bien avec rents, expenses et fiscalLaw
   * @param reset Si true, déclenche un reset des caches après sauvegarde
   * @param onConverted Appelé avec l'identifiant du bien converti dès que la conversion réussit, avant l'enregistrement
   * @returns Observable du bien créé/modifié avec toutes ses données
   */
  saveBienLocatif(
    payload: BienLocatifPayload,
    reset: boolean = true,
    onConverted?: (convertedId: number) => void,
    onPersisted?: (persisted: EstatePersisted) => void,
  ): Observable<BienLocatifPayload> {
    // Étape 1 : Déterminer localement si le type a changé
    const currentRentalType =
      payload.typeUrl === TypeUrlEnum.LMNPLMNP
        ? RentalType.FURNISHED
        : RentalType.UNFURNISHED;
    const hasTypeChanged =
      payload.id &&
      payload.originalRentalType &&
      payload.originalRentalType !== currentRentalType;

    return (
      hasTypeChanged
        ? this.convertOnBackend(payload, currentRentalType, reset).pipe(
            tap(convertedId => onConverted?.(convertedId)),
            map(convertedId => ({ ...payload, id: convertedId })),
          )
        : of(payload)
    ).pipe(
      switchMap(estate => (estate.id ? this.syncWithServer(estate) : of(estate))),
      switchMap(estate =>
        this.estateDao.postEstate(
          this.withoutUnchangedFiscalLaw(estate),
          reset,
          onPersisted,
        ),
      ),
      catchError(error => {
        console.error(
          '[BienLocatifService] Erreur lors de la sauvegarde:',
          error,
        );
        throw error;
      }),
    );
  }

  /**
   * Charge tous les biens locatifs de l'utilisateur
   * (LMNP + location non-meublée)
   *
   * @returns Observable de la liste des biens locatifs avec rents et expenses
   */
  loadBiensLocatifs(): Observable<BienLocatifDto[]> {
    return forkJoin([
      this.estateDao.getLmpLmnpsOnce(),
      this.estateDao.getUnfurnishedGlobalOnce(),
    ]).pipe(
      map(([lmnpResponse, unfurnishedResponse]) => {
        const lmnps: LmnpLmpDto[] = lmnpResponse.data?.list || [];
        const unfurnished: UnfurnishedDto[] =
          unfurnishedResponse.data?.list || [];

        // Combiner les deux listes
        return [...lmnps, ...unfurnished];
      }),
      catchError(error => {
        console.error('[BienLocatifService] Erreur lors du chargement:', error);
        return of([]);
      }),
    );
  }

  /**
   * Supprime un bien locatif
   *
   * Note : Le backend gère automatiquement la suppression en cascade
   * des rents et expenses associés
   *
   * @param userId ID de l'utilisateur
   * @param estateId ID du bien à supprimer
   * @param rentalType Type de location (FURNISHED ou UNFURNISHED)
   * @returns Observable de la réponse de suppression
   */
  deleteBienLocatif(
    userId: number,
    estateId: number,
    rentalType: RentalType,
  ): Observable<void> {
    const typeUrl =
      rentalType === RentalType.FURNISHED ? 'lmplmnp' : 'unfurnished';
    const entity = {
      id: estateId,
      userId: userId,
      typeUrl: typeUrl,
    };
    return this.estateDao.deleteEstate(entity, false);
  }

  /**
   * Change le type d'un bien locatif (nu ↔ meublé) sans le recréer : loyers,
   * charges, crédits, adresse et propriétaires sont conservés par le back, qui
   * renvoie l'identifiant du bien converti.
   */
  private convertOnBackend(
    payload: BienLocatifPayload,
    targetRentalType: RentalType,
    reset: boolean,
  ): Observable<number> {
    const sourceTypeUrl =
      targetRentalType === RentalType.FURNISHED
        ? TypeUrlEnum.UNFURNISHED
        : TypeUrlEnum.LMNPLMNP;
    const targetType =
      targetRentalType === RentalType.FURNISHED ? 'LMNP' : 'UNFURNISHED';

    return this.estateDao
      .convertRentalType(
        payload.userId,
        sourceTypeUrl,
        payload.id,
        {
          targetType,
          ...((payload as any).fiscalLaw?.type && {
            targetFiscalLaw: (payload as any).fiscalLaw.type,
          }),
        },
        reset,
      )
      .pipe(map(response => response.data.id));
  }

  private syncWithServer(
    estate: BienLocatifPayload,
  ): Observable<BienLocatifPayload> {
    return this.getEstateWithDetails(
      estate.userId,
      estate.id,
      estate.typeUrl,
    ).pipe(
      switchMap((server: any) => {
        const serverExpenses = server.data?.expenses;
        const currentRent = findCurrentRent<KeptRent>(server.data?.rents);
        const keepCurrentRent = estate.rents?.length > 0;
        const payloadExpenses = estate.expenses || [];

        const emptiedExpenses = this.managedExpenseTypes(estate.typeUrl)
          .filter(
            type => !payloadExpenses.some(expense => expense.type === type),
          )
          .map(type => findCurrentExpense<any>(serverExpenses, type))
          .filter(expense => expense?.id);

        const deletions: Observable<unknown>[] = [
          ...(currentRent && !keepCurrentRent
            ? [
                this.estateDao.deleteRealestateRent(
                  estate.userId,
                  estate.id,
                  estate.typeUrl,
                  currentRent.id,
                  false,
                ),
              ]
            : []),
          ...emptiedExpenses.map(expense =>
            this.estateDao.deleteRealestateExpense(
              estate.userId,
              estate.typeUrl,
              estate.id,
              expense.id,
            ),
          ),
        ];

        const keptRent: KeptRent | undefined =
          currentRent && keepCurrentRent
            ? {
                id: currentRent.id,
                startDate: currentRent.startDate,
                endDate: currentRent.endDate,
                rentType: currentRent.rentType,
                rentFiscalLaw: currentRent.rentFiscalLaw,
                rentedArea: currentRent.rentedArea,
                amount: currentRent.amount,
                frequency: currentRent.frequency,
              }
            : undefined;

        const synced: BienLocatifPayload = {
          ...this.withKeptRent(estate, keptRent),
          expenses: payloadExpenses.map(expense =>
            this.withServerExpense(
              expense,
              findCurrentExpense<any>(serverExpenses, expense.type),
            ),
          ),
        };

        return (
          deletions.length > 0 ? combineLatest(deletions) : of(null)
        ).pipe(map(() => synced));
      }),
    );
  }

  private managedExpenseTypes(
    typeUrl: string,
  ): ReadonlyArray<ExpenseTypeEnum> {
    return typeUrl === TypeUrlEnum.LMNPLMNP
      ? FUNNEL_RENTAL_EXPENSE_TYPES
      : FUNNEL_RENTAL_EXPENSE_TYPES.filter(
          type =>
            type !== ExpenseTypeEnum.HOUSING_TAX && type !== ExpenseTypeEnum.CFE,
        );
  }

  private withServerExpense(expense: any, serverExpense: any): any {
    if (!serverExpense) {
      return expense;
    }
    const keepsServerAmount =
      !!serverExpense.frequency &&
      isSameAmount(
        expense.amount,
        toAnnualAmount(serverExpense.amount, serverExpense.frequency),
      );
    return {
      ...serverExpense,
      startDate: serverExpense.startDate || expense.startDate,
      ...(!keepsServerAmount && {
        amount: expense.amount,
        frequency: expense.frequency,
      }),
    };
  }

  private withKeptRent(
    estate: BienLocatifPayload,
    keptRent: KeptRent | undefined,
  ): BienLocatifPayload {
    const rents = estate.rents || [];
    if (
      !keptRent?.id ||
      rents.length === 0 ||
      rents.some(rent => rent.id && rent.id !== keptRent.id)
    ) {
      return estate;
    }
    const { amount, frequency, ...keptFields } = keptRent;
    const [currentRent, ...otherRents] = rents;
    const sameMonthlyAmount =
      !!frequency &&
      isSameAmount(currentRent.amount, toMonthlyAmount(amount, frequency));
    return {
      ...estate,
      rents: [
        {
          ...currentRent,
          ...keptFields,
          startDate: keptFields.startDate || currentRent.startDate,
          ...(sameMonthlyAmount && { amount, frequency }),
        },
        ...otherRents,
      ],
    };
  }

  private withoutUnchangedFiscalLaw(
    estate: BienLocatifPayload,
  ): BienLocatifPayload {
    return estate.fiscalLaw?.id ? { ...estate, fiscalLaw: undefined } : estate;
  }

  /**
   * Récupère un bien avec tous ses détails (rents, expenses, fiscalLaw)
   *
   * @param userId ID de l'utilisateur
   * @param estateId ID du bien
   * @param typeUrl Type de bien ('lmplmnp' ou 'unfurnished')
   * @returns Observable du bien complet
   */
  private getEstateWithDetails(
    userId: number,
    estateId: number,
    typeUrl: string,
  ): Observable<any> {
    if (typeUrl === 'lmplmnp') {
      return this.estateDao.getLmpLmnp(userId, estateId);
    } else {
      return this.estateDao.getUnfurnished(userId, estateId);
    }
  }
}
